'use client';
import React, { useEffect, useRef } from 'react';

const devices = [
  { name: 'iPhone 15 / 16', icon: '📱', support: 'Full', offline: '2.4 GB', angles: 12, quality: '4K' },
  { name: 'iPad Pro / Air', icon: '⬜', support: 'Full', offline: '2.4 GB', angles: 12, quality: '4K' },
  { name: 'Android 12+', icon: '🤖', support: 'Full', offline: '2.1 GB', angles: 12, quality: '1080p' },
  { name: 'Android Tablet', icon: '📟', support: 'Full', offline: '2.1 GB', angles: 12, quality: '1080p' },
  { name: 'Mac / Windows', icon: '💻', support: 'Browser', offline: '—', angles: 12, quality: '4K' },
  { name: 'Smart TV', icon: '📺', support: 'AirPlay/Cast', offline: '—', angles: 8, quality: '4K' },
];

const cameraAngles = [
  { angle: 'Overhead 180°', use: 'Balayage, parting, sectioning', icon: '⬆' },
  { angle: 'Macro Close-up', use: 'Nail art, microblading strokes', icon: '🔍' },
  { angle: '45° Side Profile', use: 'Foil placement, cut lines', icon: '↗' },
  { angle: 'Mirror POV', use: 'Client-facing finishing, styling', icon: '🪞' },
  { angle: 'Under-Hand', use: 'Brush grip, product application', icon: '👐' },
  { angle: 'Split-Screen', use: 'Before / after, technique compare', icon: '⬜' },
];

export default function ToolsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="tools" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="section-divider mb-24" />
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="blob-periwinkle absolute top-[20%] right-[-10%] w-[400px] h-[400px] rounded-full opacity-30" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="reveal mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-DEFAULT mb-3 block">§ Tools</span>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-sans text-4xl sm:text-5xl font-extrabold text-frost tracking-tight leading-tight">
              Device specs.<br />
              <span className="font-display italic font-light text-chrome">Camera breakdown.</span>
            </h2>
            <p className="text-chrome text-sm font-light max-w-sm md:text-right">
              Every lesson filmed in a dedicated camera rig. Your device determines your experience — here's exactly what to expect.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Device Compatibility Table */}
          <div className="reveal glass-card rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-frost/5 flex items-center justify-between">
              <h3 className="text-frost font-bold text-base">Device Compatibility</h3>
              <span className="badge-beginner text-[10px] font-bold px-2.5 py-1 rounded-full">6 platforms</span>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-5 px-6 py-3 border-b border-frost/5">
              {['Device', 'Support', 'Offline', 'Angles', 'Quality']?.map((h) => (
                <span key={h} className="text-[10px] font-bold uppercase tracking-widest text-chrome-dim col-span-1 first:col-span-1">
                  {h}
                </span>
              ))}
            </div>

            {devices?.map((d, i) => (
              <div
                key={i}
                className="spec-row grid grid-cols-5 items-center px-6 py-4 border-b border-frost/5 last:border-0"
              >
                <div className="flex items-center gap-2 col-span-1">
                  <span className="text-base">{d?.icon}</span>
                  <span className="text-xs font-medium text-frost leading-tight hidden sm:block">{d?.name}</span>
                </div>
                <span className="text-xs text-sky-DEFAULT font-semibold">{d?.support}</span>
                <span className="text-xs text-chrome">{d?.offline}</span>
                <span className="text-xs text-chrome">{d?.angles}</span>
                <span className="text-xs font-bold text-periwinkle-DEFAULT">{d?.quality}</span>
              </div>
            ))}
          </div>

          {/* Camera Angles */}
          <div className="reveal glass-card rounded-2xl overflow-hidden sky-glow-hover transition-all duration-500">
            <div className="px-6 py-4 border-b border-frost/5 flex items-center justify-between">
              <h3 className="text-frost font-bold text-base">Camera Angle Library</h3>
              <span className="badge-intermediate text-[10px] font-bold px-2.5 py-1 rounded-full">12 angles</span>
            </div>

            <div className="divide-y divide-frost/5">
              {cameraAngles?.map((a, i) => (
                <div key={i} className="spec-row flex items-center gap-4 px-6 py-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-base"
                    style={{ background: 'rgba(86,204,242,0.1)', border: '1px solid rgba(86,204,242,0.2)' }}
                  >
                    {a?.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-frost text-sm font-semibold">{a?.angle}</div>
                    <div className="text-chrome-dim text-xs truncate">{a?.use}</div>
                  </div>
                  <svg className="w-4 h-4 text-chrome-dim shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Offline callout */}
        <div className="reveal mt-8 glass-card-bright rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(123,97,255,0.15)', border: '1px solid rgba(123,97,255,0.3)' }}
          >
            <svg className="w-7 h-7 text-periwinkle-DEFAULT" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-frost font-bold text-lg mb-1">Download entire modules. Work offline.</h4>
            <p className="text-chrome text-sm font-light leading-relaxed">
              No Wi-Fi between clients? No problem. Download up to <span className="text-frost font-semibold">2.4 GB</span> of lessons to your device. Full 4K playback, zero buffering, even in a basement salon.
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-3xl font-extrabold gradient-text-sky">2.4 GB</div>
            <div className="text-xs text-chrome-dim uppercase tracking-wide">max offline cache</div>
          </div>
        </div>
      </div>
    </section>
  );
}